import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { LoadingSpinner, ErrorBanner } from '../components/AdminStatus';

const API = 'https://api.udanchu.com';
const font = "'DM Sans', system-ui, sans-serif";

const s = {
  section: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '10px',
    padding: '28px',
    maxWidth: '560px',
  },
  sectionTitle: {
    color: '#e2e8f0',
    fontSize: '15px',
    fontWeight: '600',
    margin: '0 0 20px',
    fontFamily: font,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  label: {
    display: 'block',
    color: '#64748b',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    marginBottom: '6px',
    fontFamily: font,
  },
  input: {
    width: '100%',
    background: '#0f1117',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    padding: '10px 13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#0f1117',
    border: '1px solid #252a38',
    borderRadius: '7px',
    padding: '10px 13px',
    cursor: 'pointer',
  },
  fileLabelText: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileLabelTextSelected: {
    color: '#e2e8f0',
  },
  browseBtn: {
    flexShrink: 0,
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '5px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 10px',
    cursor: 'pointer',
  },
  uploadBtn: {
    marginTop: '8px',
    width: '100%',
    background: '#4f8ef7',
    border: 'none',
    borderRadius: '7px',
    color: '#fff',
    fontFamily: font,
    fontSize: '14px',
    fontWeight: '600',
    padding: '11px',
    cursor: 'pointer',
  },
  uploadBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  successBox: {
    marginTop: '20px',
    background: 'rgba(52,211,153,0.1)',
    border: '1px solid rgba(52,211,153,0.3)',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontFamily: font,
  },
  successDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#34d399',
    flexShrink: 0,
  },
  successText: {
    color: '#34d399',
    fontSize: '14px',
  },
  reviewLink: {
    display: 'inline-block',
    marginTop: '20px',
    color: '#4f8ef7',
    fontFamily: font,
    fontSize: '14px',
    fontWeight: '500',
    textDecoration: 'none',
  },
  errorWrap: {
    marginTop: '20px',
  },
};

export default function AdminUpload() {
  const [sourceName, setSourceName] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setResult(null); setError(null); }
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setResult(null);
    setError(null);

    const form = new FormData();
    form.append('file', file);
    form.append('sourceName', sourceName.trim() || file.name);

    try {
      const res = await fetch(`${API}/pdf/upload`, {
        method: 'POST',
        headers: { 'X-Admin-Key': sessionStorage.getItem('udanchu_admin_key') || '' },
        body: form,
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(json.cards_extracted ?? json.cardsExtracted ?? '?');
        setFile(null);
        setSourceName('');
        if (fileRef.current) fileRef.current.value = '';
      } else {
        setError(json.message || json.error || `Upload failed (${res.status})`);
      }
    } catch {
      setError('Could not reach the API. Check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const canUpload = !!file && !uploading;

  return (
    <AdminLayout title="Knowledge Base">
      <div style={s.section}>
        <p style={s.sectionTitle}>Upload PDF</p>

        {uploading ? (
          <LoadingSpinner message="Extracting cards from PDF..." />
        ) : (
          <div style={s.row}>
            <div>
              <label style={s.label}>SOURCE NAME</label>
              <input
                style={s.input}
                type="text"
                placeholder="e.g. Motor Vehicles Act 1988"
                value={sourceName}
                onChange={e => setSourceName(e.target.value)}
              />
            </div>

            <div>
              <label style={s.label}>PDF FILE</label>
              <label style={s.fileLabel} onClick={() => fileRef.current?.click()}>
                <span style={{ ...s.fileLabelText, ...(file ? s.fileLabelTextSelected : {}) }}>
                  {file ? file.name : 'No file selected'}
                </span>
                <button
                  type="button"
                  style={s.browseBtn}
                  onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}
                >
                  Browse
                </button>
              </label>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            <button
              style={{ ...s.uploadBtn, ...(!canUpload ? s.uploadBtnDisabled : {}) }}
              disabled={!canUpload}
              onClick={handleUpload}
            >
              Upload &amp; Extract
            </button>
          </div>
        )}

        {result !== null && !uploading && (
          <div style={s.successBox}>
            <div style={s.successDot} />
            <span style={s.successText}>
              Extraction complete — <strong>{result}</strong> cards added to the draft queue.
            </span>
          </div>
        )}

        {error && !uploading && (
          <div style={s.errorWrap}>
            <ErrorBanner error={error} onRetry={() => setError(null)} />
          </div>
        )}
      </div>

      <Link to="/admin/review" style={s.reviewLink}>→ Review Draft Cards</Link>
    </AdminLayout>
  );
}
